/* eslint-disable sonarjs/no-duplicate-string */
import { Injectable, inject } from '@angular/core';
import { FeatureToggleService } from '../../shared/feature-toggle.service';
import { AuthService } from '../authentication/auth.service';
import { PermissionFilter } from '../model';
import { AccessControl, AuthZAccessControl } from '../authentication/access-control';
import { IsActiveMatchOptions } from '@angular/router';

export interface NavigationElement {
  name?: string;
  route?: string;
  disabled?: boolean;
  icon?: string;
  isSvgIcon?: boolean;
  routerActiveExact?: boolean;
  routerActiveOptions?: Partial<IsActiveMatchOptions>;
  expanded?: boolean;
  /**
   * detectFeature is used to determine if we should run our feature toggle
   * logic on this navigation element. When this is set to true, we will attempt
   * to determine if this element should be disabled depending on a feature
   * toggle that matches the name of this element existing or being turned on.
   * If this is set to true you need to add an element in features-config.ts
   * under 'definitions' for your element to be rendered in the navigation
   */
  detectFeature?: boolean;
  elements?: Array<NavigationElement>;
  queryParams?: { [key: string]: string };
  permission?: PermissionFilter;
  requiredFeatureToggles?: Array<string>;
}

export class NavigationElements {
  public static readonly BrandingNavigation: NavigationElement = {
    name: 'Nav.Branding.Title',
    route: '/tenant/branding',
    icon: 'branding_watermark',
    permission: {
      all: ['branding.send.feature', 'hickory.BrandingManage'],
      not: ['hickory.RegionZero'],
    },
  };

  public static readonly ManageBrandsNavigation: NavigationElement = {
    name: 'Nav.Branding.Title',
    route: '/tenant/branding',
    icon: 'branding_watermark',
    permission: {
      any: ['hickory.RegionZero', 'hickory.BrandingManage'],
      not: ['branding.send.feature'],
    },
  };

  public static readonly ViewContract: NavigationElement = {
    name: 'View Contract',
    route: '/tenant/contract',
    icon: 'settings-applications',
    permission: {
      all: ['hickory.RegionZero'],
    },
  };
  public static readonly ContractManagement: NavigationElement = {
    name: 'Nav.BackOffice.ManageContracts',
    route: '/back-office/contracts',
    icon: 'settings-applications',
    permission: {
      all: ['hickory.RegionZero'],
    },
  };
  public static readonly MicrosoftTeams: NavigationElement = {
    name: 'Microsoft Teams Settings',
    route: '/tenant/microsoft-teams',
    icon: 'microsoft_teams',
    isSvgIcon: true,
    
    permission: {
      all: [AccessControl.FeatureMicrosoftTeams, AccessControl.ManageMicrosoftTeams],
    },
  };

  // only let not back office see if they have the feature and the manage permission
  public static readonly IdentityProviders: NavigationElement = {
    name: 'Identity Providers',
    icon: 'token',
    route: '/identity-providers',
    permission: {
      // require the feature toggle to be enabled
      all: [AccessControl.EnabledIdentityProviders],
      // either manage permission will suffice
      any: [AccessControl.ManageIdentityProviders, AuthZAccessControl.FullAccessToIdentityProviders],
      // back office gets their own version
      not: [AccessControl.RegionZero],
    },
  };

  // always let back office see this
  public static readonly IdentityProvidersBackOffice: NavigationElement = {
    ...this.IdentityProviders,
    permission: {
      all: [AccessControl.RegionZero],
    },
  };

  public static readonly SftpNavigation: NavigationElement = {
    name: 'Nav.Sftp.Title',
    icon: 'folder',
    expanded: false,
    permission: {
      any: ['hickory.SftpImportHistory', 'hickory.SftpUpload', 'hickory.SftpConfiguration'],
    },
    elements: [
      {
        name: 'Nav.Sftp.Upload',
        icon: 'file_upload',
        route: '/sftp/upload',
        permission: {
          all: ['hickory.SftpUpload'],
        },
      },
      {
        name: 'Nav.Sftp.History',
        route: '/sftp/history',
        icon: 'history',
        permission: {
          all: ['hickory.SftpImportHistory'],
        },
      },
      {
        name: 'Nav.Sftp.Configuration',
        route: '/sftp/configuration',
        icon: 'tune',
        permission: {
          all: ['hickory.SftpConfiguration'],
        },
      },
      {
        name: 'Nav.UpdateImportedIds',
        route: '/tenant/update-imported-ids',
        icon: 'fingerprint',
        permission: {
          all: ['hickory.UpdateImportedIds'],
        },
      },
    ],
  };

  public static readonly ReportsMenuNavigation: NavigationElement[] = [
    {
      name: 'Usage Dashboard',
      route: '/reports/dashboard',
      icon: 'dashboard',
      routerActiveExact: true,
      routerActiveOptions: {
        queryParams: 'ignored',
        paths: 'exact',
      },
      permission: {
        any: [AccessControl.ViewUsageEvents, AccessControl.RegionZero],
      },
    },
    {
      name: 'Usage Events',
      route: '/reports/usage',
      icon: 'data_usage',
      routerActiveExact: true,
      routerActiveOptions: {
        queryParams: 'ignored',
        paths: 'exact',
      },
      permission: {
        any: [AccessControl.ViewUsageEvents, AccessControl.RegionZero],
      },
    },
    {
      name: 'Usage Aggregations',
      route: '/reports/usage/aggregations',
      icon: 'analytics',
      routerActiveExact: true,
      routerActiveOptions: {
        queryParams: 'ignored',
        paths: 'subset',
      },
      permission: {
        any: [AccessControl.RegionZero],
      },
    },
  ];

  public static readonly TenantMenuNavigation: NavigationElement[] = [
    {
      name: 'Manage Roles',
      route: '/roles',
      icon: 'security',
      permission: {
        any: ['roles.manage', 'hickory.RegionZero'],
      },
    },
    {
      name: 'Custom Alert Forms',
      route: '/tenant/forms',
      icon: 'content_copy',
      permission: {
        all: ['hickory.CustomAlertFormsManage'],
      },
    },
    {
      name: 'Alert Default Settings',
      route: '/tenant/defaults',
      icon: 'tune',
      permission: {
        all: ['hickory.ChannelDefaultsManage'],
      },
    },
    this.BrandingNavigation,
    this.ManageBrandsNavigation,
    {
      name: 'Keywords ',
      route: '/tenant/keywords',
      icon: 'view_headlines',
      permission: {
        all: ['hickory.KeywordsManage'],
      },
    },
    {
      name: 'Integrations ',
      route: '/tenant/integrations',
      icon: 'route',
      permission: {
        all: ['hickory.IntegrationsManage'],
      },
    },
    {
      name: 'Third Party Authentication',
      route: '/tenant/third-party-authentication',
      icon: 'key',
      permission: {
        any: [AccessControl.ManageThirdPartyAuthentication, AccessControl.ViewThirdPartyAuthentication],
      },
    },
    NavigationElements.MicrosoftTeams,
    this.IdentityProviders,
    this.IdentityProvidersBackOffice,
  ];

  public static readonly BackOfficeMenuNavigation: NavigationElement[] = [
    {
      name: 'Nav.BackOffice.Tenant.View',
      route: '/back-office/tenants',
      routerActiveExact: true,
      icon: 'business',
      permission: {
        all: ['hickory.RegionManage'],
      },
    },
    {
      name: 'Nav.BackOffice.TenantCreate',
      route: '/admin/tenants-create',
      routerActiveExact: true,
      icon: 'add_moderator',
      permission: {
        all: ['hickory.RegionManage'],
      },
    },
    {
      name: 'Nav.Bickory.BulkActions',
      route: '/admin/bulkActions',
      routerActiveExact: true,
      icon: 'assignment',
      permission: {
        all: ['hickory.RegionManage'],
      },
    },
    {
      name: 'Nav.BackOffice.ExportAdmins',
      route: '/back-office/admins/export',
      routerActiveExact: true,
      icon: 'ios_share',
      permission: {
        all: ['hickory.ExportAdmins'],
      },
    },
    {
      name: 'Nav.BackOffice.ExportDataUsage',
      route: '/back-office/datausage/export',
      routerActiveExact: true,
      icon: 'data_usage',
      permission: {
        all: ['hickory.RegionZero'],
      },
    },
    {
      name: 'Nav.BackOffice.ConfigureBanner',
      route: '/back-office/banner',
      routerActiveExact: true,
      icon: 'warning',
      permission: {
        all: ['hickory.RegionZero'],
      },
    },
    {
      name: 'Nav.BackOffice.AlertFieldDefinitions',
      route: '/back-office/alert-field-definitions',
      routerActiveExact: true,
      icon: 'ballot',
      permission: {
        all: ['hickory.RegionZero'],
      },
    },
    NavigationElements.ContractManagement,
    {
      name: 'Nav.BackOffice.CredentialManagement',
      route: '/back-office/credentials',
      routerActiveExact: true,
      icon: 'lock',
      permission: {
        all: ['hickory.CredentialManage'],
      },
    },
    {
      name: 'Nav.BackOffice.Emails',
      route: '/back-office/emails',
      routerActiveExact: true,
      icon: 'email',
      permission: {
        all: [AccessControl.RegionZero],
      },
    },
    {
      name: 'Nav.BackOffice.PhoneNumbers',
      route: '/back-office/phones',
      routerActiveExact: true,
      icon: 'phone_enabled',
      permission: {
        all: ['hickory.RegionZero'],
      },
    },
    {
      name: 'Nav.BackOffice.Contacts',
      route: '/back-office/contacts',
      routerActiveExact: true,
      icon: 'people',
      permission: {
        all: ['hickory.RegionZero'],
      },
    },
    {
      name: 'Nav.Weather.History',
      route: '/weather/alerts',
      icon: 'thunderstorm',
      permission: {
        all: [AccessControl.RegionZero],
      },
    },
    {
      name: 'Email Templates',
      route: '/tenant/templates/emailTemplates',
      icon: 'email',
      permission: {
        all: ['hickory.RegionZero'],
      },
    },
    {
      name: 'Manage Ipaws Associations',
      route: '/back-office/ipaws-associations',
      icon: 'sensors',
      permission: {
        all: ['hickory.RegionZero'],
      },
    }
  ];

  public static readonly BackOfficeNavigation = {
    name: 'Nav.BackOffice.Title',
    permission: {
      all: ['hickory.RegionManage'],
    },
    elements: this.BackOfficeMenuNavigation,
  };

  public static readonly UnsubscribeReport: NavigationElement = {
    icon: 'summarize',
    route: '/contacts/reporting/unsubscribed',
    name: 'Unsubscribe Report',
    permission: {
      any: ['hickory.InternalMembersManage', 'hickory.AdminManage', 'hickory.RegionZero'],
    },
  };

  public static readonly ReportsNavigation: NavigationElement = {
    name: 'Nav.Reports.Title',
    icon: 'summarize',
    route: '/reports/dashboard',
    permission: { any: [AccessControl.ViewUsageEvents, AccessControl.RegionZero] },
  };

  public static readonly JobHistoryNavigation: NavigationElement = {
    name: 'Nav.Jobs.Title',
    expanded: false,
    icon: 'task',
    permission: {
      all: ['hickory.RegionZero'],
    },
    elements: [
      {
        name: 'Nav.Jobs.History',
        route: '/jobs',
        icon: 'history',
        routerActiveExact: true,
      },
      {
        name: 'Nav.Jobs.Geocoding',
        route: '/jobs/geocoding',
        icon: 'location_on',
        routerActiveExact: true,
      },
    ],
  };
}

@Injectable()
export class NavService {
  private readonly featureToggle = inject(FeatureToggleService);
  private readonly authService = inject(AuthService);

  navigation: NavigationElement = { elements: [] };

  static readonly betaQueryParam: string = 'try_beta';
  static readonly embedPathQueryParam: string = 'embed_path';

  private applyFeatureToggles(element: NavigationElement) {
    if (element.detectFeature) {
      // determine if we should show the feature, depending on the toggle
      element.disabled = !this.featureToggle.isEnabled(element.name ?? '');
    }

    if (element.requiredFeatureToggles?.length) {
      element.disabled = !element.requiredFeatureToggles.every((x) => this.featureToggle.isEnabled(x));
    }

    // check feature toggle for all child elements
    if (element.elements) {
      for (const childElement of element.elements) {
        this.applyFeatureToggles(childElement);
      }
    }
  }

  /**
   * Checks if a list of permissions is enough to show an element
   */
  public isAllowed(element: NavigationElement): boolean {
    // Disabled by feature toggle
    if (element.disabled) {
      return false;
    }

    // Check restrictions by permissions
    let allowed = !element.permission;
    if (element.permission) {
      allowed = this.authService.isSatisfied(element.permission);
    }

    // make sure that sections are not allowed to be empty
    if (!element.route && element.elements) {
      allowed = allowed && element.elements.some((e) => this.isAllowed(e));
    }

    return allowed;
  }

  /**
   * Returns the current Users' navigation structure
   */
  public getNavigation(): NavigationElement {
    return this.navigation;
  }

  setNonAdminNavigation() {
    this.navigation = {
      elements: [
        {
          name: 'Nav.Alerts.Received',
          route: '/alerts/received',
          icon: 'inbox',
          queryParams: { type: 'map' },
        },
      ],
    };
  }

  setStandardNavigation() {
    this.navigation = {
      elements: [
        // command center dashboard
        {
          name: 'Nav.Dashboard.Title',
          route: '/alerts',
          routerActiveExact: true,
          icon: 'dashboard',
          permission: {
            all: ['hickory.IncidentManagement'],
          },
        },
        // store dashboard
        {
          name: 'Nav.Dashboard.Title',
          route: '/dashboard',
          routerActiveExact: true,
          icon: 'dashboard',
          permission: {
            not: ['hickory.IncidentManagement'],
          },
        },
        {
          name: 'Nav.Alerts.Title',
          queryParams: { type: 'map' },
          permission: {
            not: ['internal-alert'],
          },
          elements: [
            {
              name: 'Nav.Communicate.SendAlert',
              route: '/alerts/create',
              routerActiveExact: true,
              icon: 'sms_failed',
              permission: {
                all: ['quick-alert.create'],
              },
            },
            {
              name: 'Nav.Communicate.SendAlertCC',
              route: '/call-center/send',
              routerActiveExact: true,
              icon: 'sms_failed',
              permission: {
                all: [AccessControl.CallCenterAccess],
              },
            },
            {
              name: 'Nav.Communicate.SendPoll',
              route: '/alerts/create/poll',
              icon: 'poll',
              permission: {
                all: ['poll.create'],
              },
            },
            {
              name: 'Nav.Alerts.Sent',
              route: '/alerts/sent',
              icon: 'send',
              queryParams: { type: 'map' },
            },
            {
              name: 'Nav.Alerts.Received',
              route: '/alerts/received',
              icon: 'inbox',
              queryParams: { type: 'map' },
            },
            {
              name: 'Nav.Communicate.Templates',
              route: '/alerts/templates',
              icon: 'folder',
              permission: {
                all: [AccessControl.AlertTemplatesManage],
              },
            },
            {
              name: 'Nav.Alerts.ScheduledAlerts',
              route: '/alerts/scheduled',
              icon: 'schedule',
              permission: {
                all: ['hickory.QuickAlertScheduledManage'],
              },
            },
            {
              name: 'Nav.Alerts.History',
              route: '/alerts/history',
              icon: 'history',
              permission: {
                all: ['hickory.QuickAlertReport'],
              },
            },
          ],
        },
        {
          name: 'Nav.Alerts.InternalTitle',
          queryParams: { type: 'map' },
          permission: {
            all: ['internal-alert'],
          },
          elements: [
            {
              name: 'Nav.Alerts.ScheduledAlerts',
              route: '/admin/scheduled',
              icon: 'schedule',
              permission: {
                all: ['hickory.QuickAlertScheduledManage'],
              },
            },
            {
              name: 'Nav.Alerts.History',
              route: '/admin/history',
              icon: 'history',
              permission: {
                all: ['hickory.QuickAlertReport'],
              },
            },
          ],
        },
        {
          name: 'Nav.Alerts.PublicTitle',
          queryParams: { type: 'map' },
          elements: [
            {
              name: 'Nav.Bickory.IPAWSAlert',
              route: '/ipaws/create',
              icon: 'speaker_phone',
              permission: {
                all: ['hickory.BalsaInlineBamboo'],
              },
            },
            {
              name: 'Nav.Bickory.PublicAlertHistory',
              route: '/admin/publicAlertHistory',
              icon: 'history',
              permission: {
                all: ['hickory.PublicAlert', 'hickory.QuickAlertReport'],
              },
            },
            {
              name: 'Nav.Bickory.PublicSubscriberHistory',
              route: '/admin/publicSubscriberAlertHistory',
              icon: 'history',
              permission: {
                all: ['hickory.PublicAlert', 'hickory.PublicSignupReport'],
              },
            },
          ],
        },
        {
          name: 'Nav.IncManage',
          elements: [
            {
              name: 'Nav.IncEvents',
              route: '/incidents',
              icon: 'assignment_late',
              permission: {
                all: ['hickory.IncidentManagement'],
                not: ['incident-v2'],
              },
            },
            // "new" incident v2 list view
            {
              name: 'Nav.IncEvents',
              route: '/incidents-v2/list',
              icon: 'assignment_late',
              permission: {
                all: [
                  AccessControl.FeatureIncidentManagement,
                  AccessControl.FeatureIncidentManagementNext,
                  AccessControl.IncidentPermissionIncidentEventView,
                ],
              },
            },
            {
              name: 'Nav.IncidentCategories',
              route: '/incidents-v2/categories/list',
              icon: 'category',
              permission: {
                all: [AccessControl.FeatureIncidentManagement, AccessControl.FeatureIncidentManagementNext],
                any: [
                  AccessControl.IncidentPermissionCategoryCreate,
                  AccessControl.IncidentPermissionCategoryEdit,
                  AccessControl.IncidentPermissionCategoryDelete,
                ],
              },
            },
            {
              name: 'Nav.SeverityLevels',
              route: '/incidents-v2/severity-levels/list',
              icon: 'low_priority',
              permission: {
                all: [AccessControl.FeatureIncidentManagement, AccessControl.FeatureIncidentManagementNext],
                any: [
                  AccessControl.IncidentPermissionSeverityLevelCreate,
                  AccessControl.IncidentPermissionSeverityLevelEdit,
                  AccessControl.IncidentPermissionSeverityLevelDelete,
                ],
              },
            },
            {
              name: 'Nav.Statuses',
              route: '/incidents-v2/event-statuses/list',
              icon: 'list',
              permission: {
                all: [AccessControl.FeatureIncidentManagement, AccessControl.FeatureIncidentManagementNext],
                any: [
                  AccessControl.IncidentPermissionEventStatusCreate,
                  AccessControl.IncidentPermissionEventStatusEdit,
                  AccessControl.IncidentPermissionEventStatusDelete,
                ],
              },
            },
            {
              name: 'Nav.Policies',
              route: '/incidents-v2/policies/list',
              icon: 'repeat',
              permission: {
                all: [AccessControl.FeatureIncidentManagement, AccessControl.FeatureIncidentManagementNext],
                any: [AccessControl.IncidentPermissionPolicyView, AccessControl.IncidentPermissionPolicyEdit],
              },
            },
            {
              name: 'Nav.Permissions',
              route: '/incidents-v2/role-permissions/list',
              icon: 'settings',
              permission: {
                all: [AccessControl.FeatureIncidentManagement, AccessControl.FeatureIncidentManagementNext],
                any: [AccessControl.IncidentPermissionRoleView, AccessControl.IncidentPermissionRoleEdit],
              },
            },
            {
              name: 'Nav.Content.View',
              route: '/content',
              icon: 'library_books',
              permission: {
                any: [
                  'content-management.contentAuthor',
                  'content-management.contentReviewer',
                  'content-management.contentViewer',
                ],
              },
            },
            {
              name: 'Nav.IncGuide',
              route: '/incident-guide',
              icon: 'assignment',
              permission: {
                all: ['hickory.bundle-incident-guide'],
              },
            },
            {
              name: 'Nav.BCP.View',
              route: '/bcp',
              icon: 'assignment',
              routerActiveExact: true,
              permission: {
                all: ['cms.bcp', 'hickory.BcpUser'],
              },
            },
            {
              name: 'Nav.BCP.Validation',
              route: '/bcp/validate',
              routerActiveExact: true,
              icon: 'business',
              permission: {
                all: ['cms.bcp', 'hickory.BcpValidation'],
              },
            },
            {
              name: 'Tasklists',
              route: '/tasklists',
              icon: 'assignment_turned_in',
              permission: {
                any: [
                  'tasklists.owned',
                  'tasklists.assigned',
                  'hickory.TasklistsManageTemplates',
                  'hickory.TasklistsActivateTemplate',
                  'hickory.TasklistsManageCreate',
                  'hickory.TasklistsManager',
                  'hickory.TasklistsMonitor',
                ],
              },
            },
          ],
        },
        {
          name: 'Resources',
          elements: [
            {
              name: 'Nav.Admins.Manage',
              route: '/admins/users',
              icon: 'supervised_user_circle',
              permission: {
                all: [AccessControl.AdminManage],
              },
            },
            {
              name: 'Nav.Bickory.ManageContacts',
              route: '/contacts/all',
              icon: 'person',
              permission: {
                all: [...AccessControl.ContactsRead],
                not: ['hickory.split-hickory-directory-by-classification'],
              },
            },
            {
              name: 'Nav.Bickory.ManageGroups',
              route: '/groups/all',
              icon: 'groups',
              permission: {
                all: ['hickory.InternalGroupsManage'],
                not: ['hickory.split-hickory-directory-by-classification'],
              },
            },
            {
              name: 'Nav.Contacts.Title',
              icon: 'person',
              route: '/contacts', // view-only contacts
              permission: {
                all: ['my-contacts.view'],
                not: [AccessControl.ContactsManage],
              },
            },
            {
              name: 'Nav.Groups.Title',
              icon: 'groups',
              route: '/groups', // view-only groups
              permission: {
                all: ['my-contacts.view'],
                not: ['hickory.InternalGroupsManage'],
              },
            },
            {
              name: 'Nav.Bickory.ManagePublicSignup',
              route: '/admin/publicSignups',
              icon: 'public',
              permission: {
                all: ['hickory.PublicSignupManage'],
              },
            },
            NavigationElements.UnsubscribeReport,
            {
              name: 'Nav.Bickory.ProfileUpdateReminder',
              route: '/admin/profileUpdateReminder',
              icon: 'event_available',
              permission: {
                all: ['hickory.InternalMemberProfileUpdateManage'],
              },
            },
            {
              name: 'Nav.Directory.Manage',
              icon: 'manage_accounts',
              permission: {
                all: ['hickory.split-hickory-directory-by-classification'],
              },
              elements: [
                {
                  name: 'Nav.Directory.Internal',
                  elements: [
                    {
                      name: 'Nav.Directory.Contacts.Internal',
                      route: '/contacts/internal',
                      icon: 'person',
                      permission: {
                        any: AccessControl.ContactsRead,
                      },
                    },
                    {
                      name: 'Nav.Directory.Groups.Internal',
                      route: '/groups/internal',
                      icon: 'groups',
                      permission: {
                        all: [AccessControl.GroupsManage],
                      },
                    },
                  ],
                },
                {
                  name: 'Nav.Directory.Public',
                  elements: [
                    {
                      name: 'Nav.Directory.Contacts.Public',
                      route: '/contacts/public',
                      icon: 'person',
                      permission: {
                        any: AccessControl.CanViewPublicInformation,
                      },
                    },
                    {
                      name: 'Nav.Directory.Groups.Public',
                      route: '/groups/public',
                      icon: 'groups',
                      permission: {
                        any: AccessControl.CanViewPublicInformation,
                      },
                    },
                  ],
                },
                {
                  name: 'Import Contacts',
                  route: '/contacts/import',
                  icon: 'upload',
                  permission: {
                    any: AccessControl.ContactsUpdate,
                  },
                },
              ],
            },
            {
              name: 'Surveys.Surveys',
              icon: 'dynamic_form',
              route: '/surveys',
              permission: {
                all: [AccessControl.ManageSurveys],
              },
            },
            NavigationElements.ReportsNavigation,
            {
              name: 'Reports.MfaAdoption.Title',
              route: '/reports/mfa-adoption',
              icon: 'shield',
              // Mirrors Hickory's access rule: (super-admin OR AdminManage) AND the
              // `hickory:mfa-adoption-report` permission. (Super-admins additionally
              // get an in-report tenant picker.)
              permission: {
                all: [AccessControl.MfaAdoptionReport],
                any: [AccessControl.AdminManage, AccessControl.RegionZero],
              },
            },
          ],
          permission: {
            all: [AccessControl.FeatureNavigationBickory],
          },
        },
        {
          name: 'Nav.CMS',
          detectFeature: true,
          elements: [],
        },
        {
          name: 'Nav.Bickory.Settings',
          permission: {},
          elements: [
            {
              name: 'Nav.Bickory.Pronunciation',
              route: '/pronunciations',
              icon: 'local_library',
              permission: {
                all: ['hickory.PronunciationManage'],
              },
            },
            {
              name: 'Nav.Bickory.SocialMedia',
              route: '/socialMedia',
              icon: 'thumb_up',
              permission: {
                all: ['hickory.SocialMediaManage'],
              },
            },
            {
              name: 'Nav.Bickory.CurrentAdmins',
              route: '/admins/logins',
              icon: 'supervised_user_circle',
              permission: {
                all: [AccessControl.AdminLoginsReport],
              },
            },
            {
              name: 'Nav.Bickory.Files',
              route: '/files',
              icon: 'attachment',
              permission: {
                all: ['hickory.FilesManage'],
              },
            },
            {
              name: 'Nav.Bickory.UsageReport',
              route: '/admin/usage',
              icon: 'donut_small',
              permission: {
                all: ['hickory.VoiceCallAndFaxReport'],
                not: [AccessControl.ViewUsageEvents],
              },
            },
            {
              name: 'Nav.Bickory.PublicSignupConfiguration',
              route: '/signupConfiguration',
              icon: 'code',
              permission: {
                all: ['hickory.PublicSignupConfiguration'],
              },
            },
            {
              name: 'Nav.Bickory.ManageRoles',
              icon: 'security',
              route: '/roles',
              permission: {
                any: ['roles.manage', 'hickory.RegionZero'],
              },
            },
            {
              name: 'Nav.Tenant.Forms.Title',
              icon: 'content_copy',
              route: '/tenant/forms',
              permission: {
                all: ['hickory.CustomAlertFormsManage'],
              },
            },
            {
              name: 'Nav.Tenant.Defaults.Title',
              icon: 'tune',
              route: '/tenant/defaults',
              permission: {
                all: ['hickory.ChannelDefaultsManage'],
              },
            },
            NavigationElements.BrandingNavigation,
            NavigationElements.ManageBrandsNavigation,
            {
              name: 'Nav.Keywords.Title',
              icon: 'view_headlines',
              route: '/tenant/keywords',
              permission: {
                all: ['hickory.KeywordsManage'],
              },
            },
            {
              name: 'Nav.Integrations.Title',
              icon: 'route',
              route: '/tenant/integrations',
              permission: {
                all: ['hickory.IntegrationsManage'],
              },
            },
            {
              name: 'Third Party Authentication',
              icon: 'key',
              route: '/tenant/third-party-authentication',
              permission: {
                any: [AccessControl.ManageThirdPartyAuthentication, AccessControl.ViewThirdPartyAuthentication],
              },
            },
            NavigationElements.ViewContract,
            NavigationElements.SftpNavigation,
            NavigationElements.JobHistoryNavigation,
            NavigationElements.MicrosoftTeams,
            NavigationElements.IdentityProviders,
            NavigationElements.IdentityProvidersBackOffice,
          ],
        },
      ],
    };
    this.applyFeatureToggles(this.navigation);
  }

  appendBackOfficeNavigation() {
    for (const element of [NavigationElements.BackOfficeNavigation]) {
      this.navigation.elements?.push(element);
    }
    this.applyFeatureToggles(this.navigation);
  }
}
